import React, { useEffect, useMemo, useState } from 'react';

import { fetchStaffList } from '../../services/api/staffApi';
import {
  referVisit,
  setActionPending,
} from '../../services/api/visitActionPendingApi';
import {
  DIRECTION_MATRIX,
  INTENTS,
  INTENTS_REQUIRING_COMMENT,
  INTENT_LABELS,
  ROLE_LABELS,
} from '../../constants/visitActionPending';
import { formatStaffName } from '../../utils/staffName';
import { summariseReferralOutcome } from '../../utils/referralReasons';

/**
 * Phase 3.1 — Unified Send Visit dialog.
 *
 * Replaces the legacy `Send for action` dialog. Branches by intent
 * category at step 1:
 *   - Refer to Doctor    (Phase 3.1 — calls POST /visits/:id/refer)
 *   - Send for Payment   (existing Action-Pending intents)
 *   - Send for Nurse-action / Other (existing matrix)
 *
 * The Refer branch posts to the dedicated /refer orchestrator which
 * decides server-side whether to bypass the Receptionist (A2 bypass
 * conditions) or park as REFERRAL_TO_DOCTOR. The frontend renders an
 * advisory hint based on a local read of the same conditions but the
 * server is authoritative.
 *
 * Props:
 *   visit          — visit row
 *   viewer         — { memberId, role }
 *   onSubmitted    — (response) => void; response is the refer/set API
 *                    payload. Caller is responsible for store updates.
 *   onCancel       — () => void
 */
export default function SendVisitDialog({
  visit, viewer, onSubmitted, onCancel,
}) {
  const callerRole = (viewer?.role || '').toUpperCase();
  const callerMemberId = viewer?.memberId;

  // ── Step 1: pick category ──────────────────────────────────────
  // Doctors/admins only refer (the legacy "Send for action" category was
  // retired for this role — refer covers all the doctor handoff paths).
  // Receptionist + Nurse keep ACTION (their only path; they can't refer).
  const isDoctor = callerRole === 'DOCTOR' || callerRole === 'ADMIN';
  const categories = useMemo(() => {
    if (isDoctor) {
      return [{
        id: 'REFER',
        label: 'Refer to another doctor',
        hint: 'Hand off the visit to a specialist; receptionist can collect payment in between.',
        icon: '🟠',
      }];
    }
    return [{
      id: 'ACTION',
      label: 'Send for action (payment / nurse / handover)',
      hint: 'Park this visit with another role for a discrete task.',
      icon: '🟡',
    }];
  }, [isDoctor]);

  const [category, setCategory] = useState(categories[0]?.id || 'ACTION');

  // ── Refer branch state ─────────────────────────────────────────
  const [targetDoctorId, setTargetDoctorId] = useState('');
  const [referralReason, setReferralReason] = useState('');
  const [paymentDueRupeesStr, setPaymentDueRupeesStr] = useState('');
  const [receptionistId, setReceptionistId] = useState('');

  // ── Action branch state (legacy matrix dialog logic) ───────────
  const allowedToRoles = Object.keys(DIRECTION_MATRIX[callerRole] || {});
  const [toRole, setToRole] = useState(allowedToRoles[0] || '');
  const allowedIntents = useMemo(
    () => DIRECTION_MATRIX[callerRole]?.[toRole] || [],
    [callerRole, toRole],
  );
  // Hide REFERRAL_TO_DOCTOR from the legacy action picker — that's
  // the Refer branch above; mixing it in would create two paths to the
  // same intent.
  const visibleActionIntents = useMemo(
    () => allowedIntents.filter((i) => i !== INTENTS.REFERRAL_TO_DOCTOR),
    [allowedIntents],
  );
  const [intent, setIntent] = useState(visibleActionIntents[0] || '');
  const [toMemberId, setToMemberId] = useState('');
  const [comment, setComment] = useState('');

  // Reset action-branch downstream state when toRole changes.
  useEffect(() => {
    if (visibleActionIntents.length === 0) {
      setIntent('');
    } else if (!visibleActionIntents.includes(intent)) {
      setIntent(visibleActionIntents[0]);
    }
    setToMemberId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toRole]);

  // ── Staff list (shared between branches) ───────────────────────
  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffError, setStaffError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setStaffLoading(true);
    setStaffError(null);
    fetchStaffList()
      .then((res) => {
        if (cancelled) return;
        if (res?.success) {
          setStaff(res.data || []);
        } else {
          setStaffError(res?.error || 'Failed to load staff');
        }
      })
      .finally(() => { if (!cancelled) setStaffLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const doctors = useMemo(
    () => (staff || [])
      .filter((m) => (m.role || '').toUpperCase() === 'DOCTOR')
      .filter((m) => (m.status || 'ACTIVE').toUpperCase() === 'ACTIVE')
      .filter((m) => m.member_id !== callerMemberId),
    [staff, callerMemberId],
  );

  const receptionists = useMemo(
    () => (staff || [])
      .filter((m) => (m.role || '').toUpperCase() === 'RECEPTIONIST')
      .filter((m) => (m.status || 'ACTIVE').toUpperCase() === 'ACTIVE'),
    [staff],
  );

  // Auto-select a sensible default receptionist for the refer flow when
  // payment is due (or another bypass-blocker is active). Prefer the
  // receptionist who originally registered the visit so it reverts back
  // to its first owner; fall back to the first active receptionist for
  // legacy visits (pre-`created_by_member_id`) and agent-created visits.
  // The doctor can change the selection before submitting.
  useEffect(() => {
    if (receptionistId) return;
    if (receptionists.length === 0) return;
    const creatorId = visit?.created_by_member_id;
    const creatorMatch = creatorId
      ? receptionists.find((r) => r.member_id === creatorId)
      : null;
    setReceptionistId((creatorMatch || receptionists[0]).member_id);
  }, [receptionists, receptionistId, visit?.created_by_member_id]);

  const actionCandidates = useMemo(
    () => (staff || [])
      .filter((m) => (m.role || '').toUpperCase() === toRole)
      .filter((m) => (m.status || 'ACTIVE').toUpperCase() === 'ACTIVE')
      .filter((m) => m.member_id !== callerMemberId),
    [staff, toRole, callerMemberId],
  );

  // ── Bypass advisory hint (frontend-only; server is authoritative) ──
  // Input + API contract are both rupees (₹, decimal allowed) — matches
  // `consultation_fee` and the rest of the money fields in the schema.
  const paymentDueRupees = (() => {
    const trimmed = (paymentDueRupeesStr || '').trim();
    if (!trimmed) return 0;
    const n = parseFloat(trimmed);
    if (!Number.isFinite(n) || n < 0) return 0;
    return n;
  })();
  const consultationPendingForCaller = (() => {
    const fee = visit?.consultation_fee;
    const collected = !!visit?.consultation_fee_collected;
    const payableTo = (
      visit?.consultation_fee_payable_to_member_id
      || visit?.doctor_member_id
    );
    return (
      fee != null
      && Number(fee) > 0
      && !collected
      && payableTo === callerMemberId
    );
  })();
  const overlayParkedWithCaller = (
    visit?.action_pending_with_member_id === callerMemberId
    && [
      INTENTS.COLLECT_PAYMENT,
      INTENTS.CLOSE_AFTER_PAYMENT,
      INTENTS.ASSIGN_BACK_TO_ME,
    ].includes(visit?.action_pending_intent)
  );
  const willBypass = (
    !!targetDoctorId
    && paymentDueRupees === 0
    && !consultationPendingForCaller
    && !overlayParkedWithCaller
  );

  // ── Submit / validation ────────────────────────────────────────
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  // Phase 3.2 — after a successful refer/park we show an explicit
  // outcome card so the doctor knows whether the visit went directly
  // to the target or parked with the receptionist (and why). The
  // dialog stays open until the doctor dismisses; only then do we
  // call onSubmitted so the parent refetches + closes.
  const [outcome, setOutcome] = useState(null);
  const [pendingResp, setPendingResp] = useState(null);

  const referValid = (
    !!targetDoctorId
    && targetDoctorId !== callerMemberId
    && (referralReason || '').trim().length > 0
    && (willBypass || !!receptionistId)
  );

  const actionCommentRequired = INTENTS_REQUIRING_COMMENT.has(intent);
  const actionValid = (
    !!toRole
    && !!intent
    && !!toMemberId
    && (!actionCommentRequired || (comment || '').trim().length > 0)
  );

  const canSubmit = category === 'REFER' ? referValid : actionValid;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setErrorMsg(null);
    try {
      let resp;
      if (category === 'REFER') {
        resp = await referVisit(visit.visit_id, {
          target_doctor_id: targetDoctorId,
          referral_reason: referralReason.trim(),
          payment_due: paymentDueRupees || 0,
          receptionist_member_id: willBypass ? null : receptionistId,
        });
        // Phase 3.1 telemetry — matches backend `metric=` log pattern.
        // Phase 3.2 — read the structured `path_taken` when present;
        // fall back to the legacy `bypassed` flag for older backends.
        const mode = resp?.path_taken
          ?? (resp?.bypassed ? 'bypass' : 'park');
        // eslint-disable-next-line no-console
        console.info(
          'metric=visit_referred value=1 visit=%s mode=%s payment_due_rupees=%s',
          visit.visit_id, mode, String(paymentDueRupees || 0),
        );
        const targetDoctor = doctors.find(
          (d) => d.member_id === targetDoctorId,
        );
        const targetName = targetDoctor ? formatStaffName(targetDoctor) : null;
        const summary = summariseReferralOutcome({
          pathTaken: mode,
          reasons: resp?.bypass_reasons_blocking || [],
          targetDoctorName: targetName,
        });
        setOutcome(summary);
        setPendingResp(resp);
        return;
      }
      resp = await setActionPending(visit.visit_id, {
        to_member_id: toMemberId,
        to_role: toRole,
        intent,
        comment: comment.trim() || null,
      });
      onSubmitted?.(resp);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      setErrorMsg(typeof detail === 'string' ? detail : 'Failed to send visit');
    } finally {
      setBusy(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────
  // Phase 3.2 — outcome card: replaces the form once a refer succeeds
  // so the doctor sees explicitly whether the visit went directly or
  // parked with the receptionist (and why). Doctor dismisses to
  // trigger onSubmitted (parent refetches + closes the dialog).
  if (outcome) {
    const isDirect = outcome.tone === 'success';
    const palette = isDirect
      ? 'border-emerald-400 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-900/30 text-emerald-900 dark:text-emerald-100'
      : 'border-amber-400 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/30 text-amber-900 dark:text-amber-100';
    return (
      <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/40 p-4">
        <div className="w-full max-w-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-lg shadow-xl p-5">
          <h2 className="text-lg font-semibold mb-3">Visit sent</h2>
          <div className={`rounded-md border px-3 py-2 mb-4 ${palette}`}>
            <div className="text-sm font-medium">
              {isDirect ? '✓ ' : 'ⓘ '}
              {outcome.headline}
            </div>
            {outcome.detail && (
              <div className="mt-1 text-xs opacity-90">
                {outcome.detail}
              </div>
            )}
            {outcome.note && (
              <div className="mt-2 pt-2 border-t border-current/20 text-[11px] leading-snug opacity-80">
                {outcome.note}
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => onSubmitted?.(pendingResp)}
              className="px-3 py-1.5 text-sm rounded-md border border-indigo-500 bg-indigo-600 text-white hover:bg-indigo-700"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/40 p-4">
        <div className="w-full max-w-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-lg shadow-xl p-5">
          <h2 className="text-lg font-semibold mb-2">Send Visit</h2>
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
            Your role ({ROLE_LABELS[callerRole] || callerRole}) cannot send this visit.
          </p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/40 p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-lg shadow-xl p-5 max-h-[90vh] overflow-y-auto"
      >
        <h2 className="text-lg font-semibold mb-1">Send Visit</h2>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
          {categories.length === 1
            ? categories[0].hint
            : 'Hand off this visit. Pick the kind of action below.'}
        </p>

        {/* ── Step 1: category picker (hidden when only one path is
            available — the title + subtitle already communicate it). */}
        {categories.length > 1 && (
          <div className="mb-4 grid gap-2">
            {categories.map((cat) => (
              <label
                key={cat.id}
                className={[
                  'flex items-start gap-2 p-3 rounded-md border cursor-pointer',
                  category === cat.id
                    ? 'border-teal-400 bg-teal-50 dark:border-teal-700 dark:bg-teal-900/30'
                    : 'border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-800',
                ].join(' ')}
              >
                <input
                  type="radio"
                  name="send-visit-category"
                  value={cat.id}
                  checked={category === cat.id}
                  onChange={() => setCategory(cat.id)}
                  className="mt-1"
                />
                <span>
                  <div className="text-sm font-medium">
                    <span className="mr-1">{cat.icon}</span>{cat.label}
                  </div>
                  <div className="text-xs text-slate-600 dark:text-slate-300">
                    {cat.hint}
                  </div>
                </span>
              </label>
            ))}
          </div>
        )}

        {/* ── Step 2A: Refer branch ───────────────────────────── */}
        {category === 'REFER' && (
          <>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">
                Refer to doctor *
              </label>
              {staffLoading ? (
                <div className="text-xs text-slate-500 dark:text-slate-400 py-2">
                  Loading…
                </div>
              ) : staffError ? (
                <div className="text-xs text-rose-600 dark:text-rose-300 py-2">
                  {staffError}
                </div>
              ) : (
                <select
                  value={targetDoctorId}
                  onChange={(e) => setTargetDoctorId(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                  required
                >
                  <option value="">— choose a doctor —</option>
                  {doctors.map((m) => (
                    <option key={m.member_id} value={m.member_id}>
                      {formatStaffName(m) || m.member_id}
                    </option>
                  ))}
                </select>
              )}
              {!staffLoading && doctors.length === 0 && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  No other active doctors in this clinic.
                </p>
              )}
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">
                Reason *
              </label>
              <textarea
                value={referralReason}
                onChange={(e) => setReferralReason(e.target.value)}
                rows={2}
                maxLength={500}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                placeholder="e.g. needs RCT — endodontist"
                required
              />
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">
                Payment due (₹)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500 dark:text-slate-400 pointer-events-none">
                  ₹
                </span>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  inputMode="decimal"
                  value={paymentDueRupeesStr}
                  onChange={(e) => setPaymentDueRupeesStr(e.target.value)}
                  className="w-full pl-7 pr-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                  placeholder="0 = no payment due (goes straight to destination doctor)"
                />
              </div>
            </div>

            {!willBypass && (
              <div className="mb-3">
                <label className="block text-sm font-medium mb-1">
                  Receptionist *
                </label>
                <select
                  value={receptionistId}
                  onChange={(e) => setReceptionistId(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                  required
                >
                  <option value="">— choose receptionist —</option>
                  {receptionists.map((m) => (
                    <option key={m.member_id} value={m.member_id}>
                      {formatStaffName(m) || m.member_id}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Visit will park here for payment / forward to the
                  destination doctor.
                </p>
              </div>
            )}

            {/* Bypass hint */}
            <div
              className={[
                'mb-3 rounded-md px-3 py-2 text-xs border',
                willBypass
                  ? 'border-emerald-400 bg-emerald-50 text-emerald-900 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-100'
                  : 'border-amber-400 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100',
              ].join(' ')}
            >
              {willBypass ? (
                <span>
                  Will go directly to the destination doctor's queue —
                  no Receptionist hop needed.
                </span>
              ) : (
                <span>
                  Will route via Receptionist
                  {paymentDueRupees > 0 && ` (₹${paymentDueRupees.toFixed(2)} due)`}
                  {overlayParkedWithCaller && ' (you currently hold an action overlay)'}
                  .
                </span>
              )}
            </div>
          </>
        )}

        {/* ── Step 2B: Action branch (legacy) ──────────────────── */}
        {category === 'ACTION' && (
          <>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Send to *</label>
              <select
                value={toRole}
                onChange={(e) => setToRole(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                required
              >
                {allowedToRoles.map((r) => (
                  <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">For *</label>
              <select
                value={intent}
                onChange={(e) => setIntent(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                required
                disabled={!visibleActionIntents.length}
              >
                {visibleActionIntents.map((i) => (
                  <option key={i} value={i}>{INTENT_LABELS[i] || i}</option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">
                {ROLE_LABELS[toRole] || 'Member'} *
              </label>
              {staffLoading ? (
                <div className="text-xs text-slate-500 dark:text-slate-400 py-2">
                  Loading staff…
                </div>
              ) : staffError ? (
                <div className="text-xs text-rose-600 dark:text-rose-300 py-2">
                  {staffError}
                </div>
              ) : (
                <select
                  value={toMemberId}
                  onChange={(e) => setToMemberId(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                  required
                >
                  <option value="">— choose —</option>
                  {actionCandidates.map((m) => (
                    <option key={m.member_id} value={m.member_id}>
                      {formatStaffName(m) || m.member_id}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">
                Comment {actionCommentRequired ? '*' : '(optional)'}
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                placeholder={
                  actionCommentRequired
                    ? 'e.g. collect ₹500 — patient waiting'
                    : 'Optional context for the receiver'
                }
                required={actionCommentRequired}
              />
            </div>
          </>
        )}

        {errorMsg && (
          <div className="mb-3 rounded-md border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-900/30 px-3 py-2 text-xs text-rose-700 dark:text-rose-200">
            {errorMsg}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || !canSubmit}
            className="px-4 py-1.5 text-sm rounded-md bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-60"
          >
            {busy ? 'Sending…' : 'Send Visit'}
          </button>
        </div>
      </form>
    </div>
  );
}
